import STATISTICS_API from 'api/statistics/v2'
import omit from 'lodash/omit'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import prepareFilterParams from 'utils/prepareFilterParams'
import { notificationShow } from 'modules/Notifications/actions'
import i18n from 'i18next'
import {
  CREATE_GROUP,
  DROP_TABLE_DATA,
  FETCH_ACCOUNTS,
  FETCH_CHART,
  FETCH_CHART_START,
  FETCH_FAIL,
  FETCH_GROUPS,
  FETCH_TABLE,
  FETCH_TABLE_START,
  SET_CHART,
  SET_TABLE,
  UPDATE_ACCOUNT_BY_ID,
  UPDATE_ACCOUNT_GROUPS,
  UPDATE_ACCOUNTS,
  UPDATE_GROUP,
  UPDATE_GROUP_BY_ID,
  UPDATE_GROUPS,
  UPDATE_GROUPS_BULK,
} from 'modules/Statistics/types'

function* getFilters() {
  const filtersState = yield select((state) => ({
    ...state['statistics/filters'].filters,
    ...state['statistics'].filters,
  }))

  return omit(prepareFilterParams(filtersState), ['include_vat'])
}

function* statisticsFetchChart({ payload: { isDemoMode, params: additionalParams } }) {
  try {
    const filtersState = yield call(getFilters)

    const params = { ...filtersState, ...additionalParams }

    yield put({ type: FETCH_CHART_START, payload: 'chart' })

    const { response, error } = yield call(
      isDemoMode ? STATISTICS_API.fetchChartDemo : STATISTICS_API.fetchChart,
      {
        params: params,
      },
    )

    if (error) {
      throw error
    }

    const { data = [], summary = {} } = response

    yield put({ type: SET_CHART, payload: { data, summary } })
  } catch (error) {
    yield put({ type: FETCH_FAIL, payload: 'chart' })
    console.logXHR(error)
  }
}

function* fetchStatisticsTable({ payload: { group_by, limit = 50, offset = 0 } }) {
  const isDemoMode = localStorage.getItem('isDemoMode') === 'true'

  try {
    yield put({ type: FETCH_TABLE_START, payload: 'table' })

    let METHOD

    switch (group_by) {
      case 'accounts':
        METHOD = isDemoMode
          ? STATISTICS_API.fetchAccountsStatisticsDemo
          : STATISTICS_API.fetchAccountsStatistics
        break
      case 'networks':
        METHOD = isDemoMode
          ? STATISTICS_API.fetchNetworksStatisticsDemo
          : STATISTICS_API.fetchNetworksStatistics
        break
      case 'groups':
        METHOD = isDemoMode
          ? STATISTICS_API.fetchGroupsStatisticsDemo
          : STATISTICS_API.fetchGroupsStatistics
        break
    }

    const filtersState = yield call(getFilters)

    const params = {
      ...filtersState,
      limit: limit,
      offset: offset,
    }

    const { response, error } = yield call(METHOD, {
      params: params,
    })

    if (error) {
      throw error
    }

    const { data = [], summary, pagination } = response

    yield put({
      type: SET_TABLE,
      payload: { data, summary, pagination },
    })
  } catch (error) {
    yield put({ type: FETCH_FAIL, payload: 'table' })
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  }
}

function* fetchGroups() {
  try {
    const { response, error } = yield call(STATISTICS_API.fetchGroups)

    if (error) {
      throw error
    }

    yield put({ type: UPDATE_GROUPS, payload: response.data })
  } catch (error) {
    console.logXHR(error)
  }
}

function* fetchAccounts() {
  try {
    const { response, error } = yield call(STATISTICS_API.fetchAccounts)

    if (error) {
      throw error
    }

    yield put({ type: UPDATE_ACCOUNTS, payload: response.data })
  } catch (error) {
    console.logXHR(error)
  }
}

function* createGroup({ payload: { newGroupData } }) {
  try {
    const { error } = yield call(STATISTICS_API.createGroup, {
      data: { ...newGroupData, account_ids: newGroupData.account_ids ?? [] },
    })

    if (error) {
      throw error
    }

    yield put({
      type: DROP_TABLE_DATA,
    })

    yield put({
      type: FETCH_TABLE,
      payload: { group_by: 'groups' },
    })
    yield put(
      notificationShow({
        type: 'success',
        message: i18n.t('statistics::table.accounts_manager.group_creation', {
          context: 'success',
        }),
      }),
    )
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: i18n.t('statistics::table.accounts_manager.group_creation', {
          context: 'failed',
        }),
      }),
    )
    console.logXHR(error)
  }
}

function* updateGroup({ payload: { id, newGroupData } }) {
  try {
    const { error } = yield call(STATISTICS_API.updateGroup, {
      id,
      data: newGroupData,
    })

    if (error) {
      throw error
    }

    const filtersState = yield call(getFilters)

    const { response: groupsDataResponse, error: groupsDataError } = yield call(
      STATISTICS_API.fetchGroupsStatistics,
      {
        params: { ...filtersState, account_group_ids: [id] },
      },
    )

    if (groupsDataError) {
      throw groupsDataError
    }

    yield all(
      groupsDataResponse.data.map((groupData) =>
        put({
          type: UPDATE_GROUP_BY_ID,
          payload: groupData,
        }),
      ),
    )

    yield put(
      notificationShow({
        type: 'success',
        message: i18n.t('statistics::table.accounts_manager.group_update', {
          context: 'success',
        }),
      }),
    )
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: i18n.t('statistics::table.accounts_manager.group_update', {
          context: 'failed',
        }),
      }),
    )
    console.logXHR(error)
  }
}

function* updateGroupsBulk({ payload: { groups } }) {
  try {
    yield all(
      groups.map((group) =>
        call(STATISTICS_API.updateGroup, {
          id: group.id,
          data: omit(group, ['id']),
        }),
      ),
    )

    const filtersState = yield call(getFilters)

    const { response: groupsDataResponse, error: groupsDataError } = yield call(
      STATISTICS_API.fetchGroupsStatistics,
      {
        params: { ...filtersState, account_group_ids: groups.map((g) => g.id) },
      },
    )

    if (groupsDataError) {
      throw groupsDataError
    }

    yield all(
      groupsDataResponse.data.map((groupData) =>
        put({
          type: UPDATE_GROUP_BY_ID,
          payload: groupData,
        }),
      ),
    )
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_update_failed',
      }),
    )
    console.logXHR(error)
  }
}

function* updateAccountGroups({ payload: { account, groupIds, groupBy } }) {
  try {
    const { response: groupsResponse, error: groupsError } = yield call(STATISTICS_API.fetchGroups)
    if (groupsError) throw groupsError

    const freshGroups = groupsResponse.data ?? []
    const prevGroupIds = (account?.groups ?? []).map((g) => String(g?.id))
    const nextGroupIds = groupIds

    const addedIds = nextGroupIds.filter((id) => !prevGroupIds.includes(id))
    const removedIds = prevGroupIds.filter((id) => !nextGroupIds.includes(id))
    const changedIds = [...addedIds, ...removedIds]

    if (changedIds.length === 0) return

    const getAccountIds = (group) =>
      (Array.isArray(group?.accounts) ? group.accounts : []).map((a) =>
        String(a?.account?.id ?? a?.id),
      )

    const updates = changedIds
      .map((groupId) => {
        const group = freshGroups.find((g) => String(g?.id) === String(groupId))
        if (!group) return null

        const currentAccountIds = getAccountIds(group)
        const isAdding = addedIds.includes(groupId)
        const nextAccountIds = isAdding
          ? [...new window.Set([...currentAccountIds, String(account.id)])]
          : currentAccountIds.filter((id) => id !== String(account.id))

        return {
          id: group.id,
          name: group.name,
          account_ids: nextAccountIds,
        }
      })
      .filter(Boolean)

    yield all(
      updates.map((group) =>
        call(STATISTICS_API.updateGroup, {
          id: group.id,
          data: omit(group, ['id']),
        }),
      ),
    )

    const newAccountGroups = freshGroups
      .filter((g) => nextGroupIds.includes(String(g?.id)))
      .map((g) => ({ id: g.id, name: g.name }))

    yield put({
      type: UPDATE_ACCOUNT_BY_ID,
      payload: {
        id: account.id,
        data: {
          ...account,
          groups: newAccountGroups,
        },
      },
    })

    yield put({ type: FETCH_GROUPS })
    yield put({ type: DROP_TABLE_DATA })
    yield put({ type: FETCH_TABLE, payload: { group_by: groupBy } })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_update_failed',
      }),
    )
    console.logXHR(error)
  }
}

export default function* Sagas() {
  yield takeLatest(FETCH_CHART, statisticsFetchChart)
  yield takeLatest(FETCH_TABLE, fetchStatisticsTable)
  yield takeLatest(FETCH_GROUPS, fetchGroups)
  yield takeLatest(FETCH_ACCOUNTS, fetchAccounts)
  yield takeLatest(CREATE_GROUP, createGroup)
  yield takeLatest(UPDATE_GROUP, updateGroup)
  yield takeLatest(UPDATE_GROUPS_BULK, updateGroupsBulk)
  yield takeLatest(UPDATE_ACCOUNT_GROUPS, updateAccountGroups)
}
